import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { Scope, showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'

interface UpdateContactScopeData {
  contactId: string
  scope: Scope
}

const updateContactScope = async ({
  contactId,
  scope,
}: UpdateContactScopeData): Promise<void> => {
  await api.put(`/contacts/${contactId}/scope`, { scope })
}

export const useUpdateContactScope = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, UpdateContactScopeData, {
    previousProfile: unknown
    previousContact: unknown
  }>({
    mutationFn: updateContactScope,
    onMutate: async (variables) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['profile', variables.contactId] }),
        queryClient.cancelQueries({ queryKey: ['contact', variables.contactId] }),
      ])

      const previousProfile = queryClient.getQueryData(['profile', variables.contactId])
      const previousContact = queryClient.getQueryData(['contact', variables.contactId])

      queryClient.setQueryData(['profile', variables.contactId], (old: any) =>
        old ? { ...old, scope: variables.scope } : old,
      )
      queryClient.setQueryData(['contact', variables.contactId], (old: any) =>
        old ? { ...old, scope: variables.scope } : old,
      )

      return { previousProfile, previousContact }
    },
    onError: (error, variables, context) => {
      if (context?.previousProfile !== undefined) {
        queryClient.setQueryData(['profile', variables.contactId], context.previousProfile)
      }
      if (context?.previousContact !== undefined) {
        queryClient.setQueryData(['contact', variables.contactId], context.previousContact)
      }
      showApiErrorToast(t, error)
    },
    onSuccess: (_, _variables) => {
      showSuccessToast(t('toast.success'), t('toast.scopeChanged'))
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contact', variables.contactId], exact: true }),
        queryClient.invalidateQueries({ queryKey: ['profile', variables.contactId], exact: true }),
      ])
    }
  })
}

